import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PieChartIcon from "@mui/icons-material/PieChart";
import PrintIcon from "@mui/icons-material/Print";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import Skeleton from "@mui/material/Skeleton";
import { Line } from "react-chartjs-2";
import toRibuan from "../../../utils/toRibuan";
import toRupiah from "../../../utils/toRupiah";
import CardLaporanKalkulasi from "../../../components/CardLaporanKalkulasi/CardLaporanKalkulasi";
import { ContentUkm, UserResponse } from "../../../constants/types";
import CalendarFilter from "../../../components/Calendar/CalendarFilter";
import LaporanTabs, { LaporanTabKey } from "./LaporanTabs";
import {
    GetLaporanLabaRugiResponse,
    GetLaporanTotalResponse,
} from "../../../constants/types";

interface LaporanMainContentProps {
    chartData: {
        labels: string[];
        datasets: {
            label: string;
            data: number[];
            borderColor: string;
            backgroundColor: string;
            yAxisID: string;
            tension: number;
            pointRadius: number;
        }[];
    };
    chartOptions: object;
    changeTab: (slug: LaporanTabKey) => void;
    closeTooltipProdukTerjual: () => void;
    closeTooltipTotalPendapatan: () => void;
    closeTooltipTotalTransaksi: () => void;
    endDate: Date | null;
    handleDateRangeChange: (update: [Date | null, Date | null]) => void;
    handleTabChange: (slug: LaporanTabKey) => void;
    isLoadingGrafikPenjualan: boolean;
    isLoadingLaporanLabaRugi: boolean;
    isLoadingLaporanTotal: boolean;
    isPhoneScreen: boolean;
    isTabletScreen: boolean;
    laporanLabaRugi?: GetLaporanLabaRugiResponse["data"];
    laporanTotal?: GetLaporanTotalResponse["data"];
    now: string;
    onOpenModalAlertPro: () => void;
    onOpenModalPrintTutupBuku: () => void;
    onOpenModalUnduhLaporan: () => void;
    openTooltipProdukTerjual: boolean;
    openTooltipTotalPendapatan: boolean;
    openTooltipTotalTransaksi: boolean;
    startDate: Date | null;
    tanggalAkhir: string;
    tanggalAwal: string;
    today: Date;
    toggleTooltipProdukTerjual: () => void;
    toggleTooltipTotalPendapatan: () => void;
    toggleTooltipTotalTransaksi: () => void;
    ukmData?: ContentUkm;
    user?: UserResponse["data"];
    tabSlug: string;
}

function subtractMonths(numOfMonths: number, date = new Date()) {
    const result = new Date(date);
    result.setMonth(result.getMonth() - numOfMonths);
    return result;
}

function subtractYears(numOfYears: number, date = new Date()) {
    const result = new Date(date);
    result.setFullYear(result.getFullYear() - numOfYears);
    return result;
}

const tooltipTotalPendapatan =
    "Total Pendapatan menunjukkan total keuntunganmu pada periode waktu tertentu.";
const tooltipTotalTransaksi =
    "Total Transaksi menunjukkan berapa banyak transaksi yang dilakukan pada periode waktu tertentu.";
const tooltipProdukTerjual =
    "Jumlah Stok Terjual menunjukkan jumlah barang yang terjual pada periode waktu tertentu.";

const currencyIcon = (
    <Typography
        variant="h6"
        color="primary"
        fontWeight="bold"
        sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
        }}
    >
        Rp
    </Typography>
);

const chartLegend = [
    { label: "Total Pendapatan", color: "#45A779" },
    { label: "Jumlah Transaksi", color: "#e8d43a" },
];

const LaporanMainContent = ({
    chartData,
    chartOptions,
    changeTab,
    closeTooltipProdukTerjual,
    closeTooltipTotalPendapatan,
    closeTooltipTotalTransaksi,
    endDate,
    handleDateRangeChange,
    handleTabChange,
    isLoadingGrafikPenjualan,
    isLoadingLaporanLabaRugi,
    isLoadingLaporanTotal,
    isPhoneScreen,
    isTabletScreen,
    laporanLabaRugi,
    laporanTotal,
    now,
    onOpenModalAlertPro,
    onOpenModalPrintTutupBuku,
    onOpenModalUnduhLaporan,
    openTooltipProdukTerjual,
    openTooltipTotalPendapatan,
    openTooltipTotalTransaksi,
    startDate,
    tanggalAkhir,
    tanggalAwal,
    today,
    toggleTooltipProdukTerjual,
    toggleTooltipTotalPendapatan,
    toggleTooltipTotalTransaksi,
    ukmData,
    user,
    tabSlug,
}: LaporanMainContentProps) => {

    return (
        <Box>
            <Stack
                direction={{
                    xs: "column",
                    sm: "column",
                    md: "column",
                    lg: "row",
                }}
                display="flex"
                justifyContent="space-between"
                spacing={2}
                marginY={2}
            >
                <Typography id="laporan" variant="h6" fontWeight="bold">
                    Laporan
                </Typography>
                
                <Stack direction="row" spacing={1} justifyContent="end" display="flex">
                    <Button
                        disabled={user && !user.akses.laporan.includes(33)}
                        variant="outlined"
                        startIcon={
                            isPhoneScreen || isTabletScreen ? undefined : (
                                <ReceiptIcon fontSize="medium" />
                            )
                        }
                        size="small"
                        sx={{ width: "fit-content", backgroundColor: "white" }}
                        onClick={() => changeTab("invoice")}
                    >
                        {isPhoneScreen || isTabletScreen ? (
                            <ReceiptIcon fontSize="medium" />
                        ) : (
                            "Laporan Invoice"
                        )}
                    </Button>
                    <Button
                        disabled={user && !user.akses.laporan.includes(34)}
                        variant="outlined"
                        startIcon={
                            isPhoneScreen || isTabletScreen ? undefined : (
                                <MoveToInboxIcon />
                            )
                        }
                        size="small"
                        sx={{ width: "fit-content", backgroundColor: "white" }}
                        onClick={() => changeTab("mutasi")}
                    >
                        {isPhoneScreen || isTabletScreen ? (
                            <MoveToInboxIcon />
                        ) : (
                            "Laporan Mutasi"
                        )}
                    </Button>
                    <Button
                        disabled={user && !user.akses.laporan.includes(35)}
                        variant="outlined"
                        startIcon={
                            isPhoneScreen || isTabletScreen ? undefined : (
                                <ReceiptIcon fontSize="medium" />
                            )
                        }
                        size="small"
                        sx={{ width: "fit-content", backgroundColor: "white" }}
                        onClick={() =>
                            changeTab("stokTerjual")
                        }
                    >
                        {isPhoneScreen || isTabletScreen ? (
                            <PieChartIcon fontSize="medium" />
                        ) : (
                            "Stok Terjual"
                        )}
                    </Button>

                    <Button
                        disabled={user && !user.akses.laporan.includes(31)}
                        variant="contained"
                        onClick={() => {
                            if (ukmData?.isPro === 1) {
                                onOpenModalUnduhLaporan();
                            } else {
                                onOpenModalAlertPro();
                            }
                        }}
                        startIcon={
                            isPhoneScreen || isTabletScreen ? undefined : (
                                <CloudDownloadIcon />
                            )
                        }
                        size="small"
                        sx={{ width: "fit-content" }}
                    >
                        <Stack direction="row" gap={1} alignItems="center">
                            {isPhoneScreen || isTabletScreen ? (
                                <CloudDownloadIcon />
                            ) : (
                                "Unduh Laporan"
                            )}
                            {ukmData?.isPro === 1 ? null : (
                                <Chip
                                    label="Standar"
                                    size="small"
                                    sx={{
                                        backgroundColor: "#fff",
                                        color: "primary.main",
                                        borderRadius: 1,
                                    }}
                                />
                            )}
                        </Stack>
                    </Button>

                    <Button
                        disabled={user && !user.akses.laporan.includes(36)}
                        startIcon={
                            isPhoneScreen || isTabletScreen ? undefined : (
                                <PrintIcon />
                            )
                        }
                        size="small"
                        variant="contained"
                        onClick={() => {
                            if (ukmData?.isPro === 1) {
                                onOpenModalPrintTutupBuku();
                            } else {
                                onOpenModalAlertPro();
                            }
                        }}
                        sx={{ width: "fit-content" }}
                    >
                        {isPhoneScreen || isTabletScreen ? (
                            <PrintIcon />
                        ) : (
                            "Print Tutup Buku"
                        )}
                    </Button>
                </Stack>
            </Stack>

            <Stack mb={1}>
                <Typography>
                    Pantau seluruh ringkasan data dan rincian laporan secara lengkap.
                </Typography>
            </Stack>

            <Box
                width={isPhoneScreen ? "100%" : 300}
                marginBottom={3}
            >
                <CalendarFilter
                    value={{
                        from: startDate ?? undefined,
                        to: endDate ?? undefined,
                    }}
                    onApply={(range) =>
                        handleDateRangeChange([
                            range.from ?? null,
                            range.to ?? null,
                        ])
                    }
                    maxDate={today}
                    minDate={
                        ukmData?.isPro === 1
                            ? subtractYears(1)
                            : subtractMonths(3)
                    }
                    disabledPresets={
                        ukmData?.isPro === 1 ? [] : ["1 tahun terakhir"]
                    }
                    onSelectDisabledPreset={onOpenModalAlertPro}
                />
            </Box>

            <Grid container spacing={3}>
                {user?.akses?.rekapPenjualan?.includes(81) && (
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <CardLaporanKalkulasi
                            namaCard="Total Pendapatan"
                            onClickAway={closeTooltipTotalPendapatan}
                            isLoading={isLoadingLaporanTotal}
                            tooltipTitle={tooltipTotalPendapatan}
                            onClose={closeTooltipTotalPendapatan}
                            isOpenTooltip={openTooltipTotalPendapatan}
                            onClickTooltip={toggleTooltipTotalPendapatan}
                            isiCard={toRupiah(Number(laporanTotal?.totalPendapatan))}
                            icon={currencyIcon}
                        />
                    </Grid>
                )}

                {user?.akses?.rekapPenjualan?.includes(82) && (
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <CardLaporanKalkulasi
                            namaCard="Total Transaksi"
                            onClickAway={closeTooltipTotalTransaksi}
                            isLoading={isLoadingLaporanTotal}
                            tooltipTitle={tooltipTotalTransaksi}
                            onClose={closeTooltipTotalTransaksi}
                            isOpenTooltip={openTooltipTotalTransaksi}
                            onClickTooltip={toggleTooltipTotalTransaksi}
                            isiCard={`${toRibuan(
                                Number(laporanTotal?.totalTransaksi),
                            )} Transaksi`}
                            icon={
                                <InsertChartIcon
                                    sx={{
                                        color: "primary.main",
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                    }}
                                />
                            }
                        />
                    </Grid>
                )}

                {user?.akses?.rekapPenjualan?.includes(87) && (
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <CardLaporanKalkulasi
                            namaCard="Jumlah Stok Terjual"
                            onClickAway={closeTooltipProdukTerjual}
                            isLoading={isLoadingLaporanTotal}
                            tooltipTitle={tooltipProdukTerjual}
                            onClose={closeTooltipProdukTerjual}
                            isOpenTooltip={openTooltipProdukTerjual}
                            onClickTooltip={toggleTooltipProdukTerjual}
                            isiCard={`${toRibuan(
                                Number(laporanTotal?.jumlahStokTerjual),
                            )} Stok`}
                            icon={
                                <PieChartIcon
                                    sx={{
                                        color: "primary.main",
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                    }}
                                />
                            }
                        />
                    </Grid>
                )}

                {user?.akses?.rekapPenjualan?.includes(84) && (
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <CardLaporanKalkulasi
                            namaCard="Laba Kotor"
                            onClickAway={() => undefined}
                            tooltipTitle=""
                            onClose={() => undefined}
                            isOpenTooltip={false}
                            onClickTooltip={() => undefined}
                            isiCard={toRupiah(
                                Number(laporanLabaRugi?.content["Gross Profit"]),
                            )}
                            isLoading={isLoadingLaporanLabaRugi}
                            icon={currencyIcon}
                        />
                    </Grid>
                )}

                {user?.akses?.rekapPenjualan?.includes(85) && (
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <CardLaporanKalkulasi
                            namaCard="Total Pengeluaran"
                            onClickAway={() => undefined}
                            tooltipTitle=""
                            onClose={() => undefined}
                            isOpenTooltip={false}
                            onClickTooltip={() => undefined}
                            isiCard={toRupiah(
                                Number(
                                    laporanLabaRugi?.content[
                                        "Total dari Operational Expense"
                                    ],
                                ),
                            )}
                            isLoading={isLoadingLaporanLabaRugi}
                            icon={currencyIcon}
                        />
                    </Grid>
                )}

                {user?.akses?.rekapPenjualan?.includes(86) && (
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <CardLaporanKalkulasi
                            namaCard="Laba Bersih (Rugi)"
                            onClickAway={() => undefined}
                            tooltipTitle=""
                            onClose={() => undefined}
                            isOpenTooltip={false}
                            onClickTooltip={() => undefined}
                            isiCard={
                                laporanLabaRugi
                                    ? laporanLabaRugi.content["Profit (Loss)"] >= 0
                                        ? toRibuan(
                                              String(
                                                  laporanLabaRugi.content[
                                                      "Profit (Loss)"
                                                  ],
                                              ),
                                          )
                                        : `( ${toRibuan(
                                              String(
                                                  laporanLabaRugi.content[
                                                      "Profit (Loss)"
                                                  ] * -1,
                                              ),
                                          )} )`
                                    : ""
                            }
                            isLoading={isLoadingLaporanLabaRugi}
                            icon={currencyIcon}
                        />
                    </Grid>
                )}
            </Grid>

            <Grid mt={3}>
                <Box
                    sx={{
                        backgroundColor: "#FFFFFF",
                        border: 1,
                        borderColor: "#d4d4d4",
                        borderRadius: 1,
                        width: "100%",
                        padding: { xs: 1, lg: 2 },
                    }}
                >
                    <Box sx={{ width: "100%" }}>
                        {isLoadingGrafikPenjualan ? (
                            <Skeleton variant="rectangular" width="100%" height={300} />
                        ) : (
                            <Line
                                data={chartData}
                                options={chartOptions}
                                width="100%"
                                height="300"
                            />
                        )}
                    </Box>
                    <Stack
                        marginTop={1}
                        direction="row"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                    >
                        {chartLegend.map((item) => (
                            <Stack direction="row" key={item.label}>
                                <Box
                                    sx={{
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        backgroundColor: item.color,
                                    }}
                                ></Box>
                                <Typography marginLeft={1} variant="subtitle2">
                                    {item.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            </Grid>

            <LaporanTabs
                tabSlug={tabSlug}
                onTabChange={handleTabChange}
                tanggalAwal={tanggalAwal}
                tanggalAkhir={tanggalAkhir}
                user={user}
            />
        </Box>
    );
};

export default LaporanMainContent;
